/**
 * PersonaTab.tsx — `definition.persona` (ADR-084 D1), the agentic editor's
 * Persona tab (design doc §Persona tab, E12-S3a). Three flat fields, no
 * sub-sections — D1's `Persona` has exactly `name` / `voice_summary` /
 * `disclosure_rule`.
 */

import { useTranslation } from 'react-i18next';
import {
  AGENT_CONFIG_MAX_LENGTHS,
  type Persona,
  type SkillVersionFieldError,
} from '../../api/types';
import { CounterField } from './agenticFieldHelpers';

export interface PersonaTabProps {
  persona: Persona;
  onChange: (next: Persona) => void;
  disabled?: boolean;
  fieldErrors: SkillVersionFieldError[];
}

const errorFor = (fieldErrors: SkillVersionFieldError[], path: string): string | undefined =>
  fieldErrors.find((error) => error.field_path === path)?.reason;

export const PersonaTab = ({
  persona,
  onChange,
  disabled = undefined,
  fieldErrors,
}: PersonaTabProps) => {
  const { t } = useTranslation();
  return (
    <section aria-labelledby="agentic-persona-heading" className="flex flex-col gap-4">
      <h3 id="agentic-persona-heading" className="text-h3 text-ink-1">
        {t('config.editAgentic.persona.heading')}
      </h3>
      <CounterField
        id="agentic-persona-name"
        label={t('config.editAgentic.persona.name')}
        value={persona.name}
        maxLength={AGENT_CONFIG_MAX_LENGTHS.personaName}
        onChange={(next) => onChange({ ...persona, name: next })}
        disabled={disabled}
        error={errorFor(fieldErrors, 'definition.persona.name')}
      />
      <CounterField
        id="agentic-persona-voice-summary"
        label={t('config.editAgentic.persona.voiceSummary')}
        value={persona.voice_summary}
        maxLength={AGENT_CONFIG_MAX_LENGTHS.personaVoiceSummary}
        onChange={(next) => onChange({ ...persona, voice_summary: next })}
        disabled={disabled}
        multiline
        rows={4}
        error={errorFor(fieldErrors, 'definition.persona.voice_summary')}
      />
      <CounterField
        id="agentic-persona-disclosure-rule"
        label={t('config.editAgentic.persona.disclosureRule')}
        value={persona.disclosure_rule}
        maxLength={AGENT_CONFIG_MAX_LENGTHS.personaDisclosureRule}
        onChange={(next) => onChange({ ...persona, disclosure_rule: next })}
        disabled={disabled}
        multiline
        rows={4}
        error={errorFor(fieldErrors, 'definition.persona.disclosure_rule')}
      />
    </section>
  );
};
